import React, { useEffect, useState } from "react";
import { withStore } from "store/storeHandler";
import LogOutContainer from "container/LogOutContainer";
import ErrorCatcher from "container/ErrorCatcher";
import DynamicHeader from "container/DynamicHeader";
import { CookiesProvider } from "react-cookie";
import RootSnackbar from "container/snackbar/RootSnackbar";
import MainRouter from "container/MainRouter";
import NoticeModalView from "presentational/common/modals/NoticeModalView";
import ScrollToTop from "container/ScrollToTop";
import { BrowserRouter } from "react-router-dom";
import { withToken } from "lib/forceTokenRenewComponent";
import { changeSentryConfig } from "lib/sentryHelper";
import { getEssentialUrl } from "lib/urlQueryParser";
import Service from "lib/service-amp";

const GOOGLE_CALLBACK_PATH = getEssentialUrl({ type: "googleCallback" })();

function App(props) {
  const {
    username,
    viewerLocation,
    MyAccountActions,
    UserDeviceActions,
    UserProfileActions,
    CognitoAuthActions,
    AppUpdateActions,
    GoogleAnalyticsActions,
    AppErrorActions
  } = props;
  const { forceRenewTokenWT } = props;
  const [isCogniteAuthCheckDone, setIsCogniteAuthCheckDone] = useState(false);
  const [isTokenValidationChekDone, setIsTokenValidationChekDone] =
    useState(false);
  const [isBackendServerBroken, setIsBackendServerBroken] = useState(false);
  const [usernameForSentry, setUsernameForSentry] = useState(null);

  useEffect(() => {
    initApp();
  }, []);

  useEffect(() => {
    checkUsernameChanged();
  });

  const initApp = async () => {
    preventRightClickMenuEvent();
    getDeviceLocation();
    setAppStartTimeRecord();
    await Promise.all([initCognitoAuth()]);
    await validateToken();
  };

  const preventRightClickMenuEvent = () => {
    /**
     * PC화면의 마우스 우클릭과 안드로이드의 3초 터치 지속시 나오는 contextmenu 의 화면 출력을 비활성화 하는 함수.
     * 아이폰에서는 일부 기능 비활성화 안되지만 하단의 목적에 영향주지 않기에 사용중.
     * 목적 : 이미지 crop시 안드로이드에서(아이폰은 괜찮았던거 가틈) 미세한 이미지 편집 위해 크롭 핸들을
     *       오래 클릭하고 있으면 화면에 튀어나오는 contextmenu 로 인해 ux 사용성 떨어지던 문제 해결용.
     */

    if (process.env.NODE_ENV !== "production") return null;

    window.document.body.addEventListener("contextmenu", function (e) {
      e.preventDefault();
    });
  };

  const checkUsernameChanged = () => {
    if (username !== usernameForSentry) {
      changeSentryConfig({ username, viewerLocation });
      setUsernameForSentry(username);
    }
  };

  const getDeviceLocation = async () => {
    if (viewerLocation) return null;
    try {
      await UserDeviceActions.editViewerLocation();
    } catch (error) {
      AppErrorActions.setSafeAppError(error);
    }
  };

  const setAppStartTimeRecord = () => {
    AppUpdateActions.recordStartTime();
  };

  const validateToken = async () => {
    if (isBackendServerBroken) return null;
    await forceRenewTokenWT();
    setIsTokenValidationChekDone(true);
  };

  const forceRemoveToken = async () => {
    /**
     * db에 사용자 데이터가 없어서 사용자가 어느 로그인서비스 사용중인지 모르는 상황이므로
     * cognito 외 google등 다른 로그인서비스 사용시 하단 코드에 토큰제거코드 추가해아함.
     */
    await Service.logOut();
  };

  const initCognitoAuth = async () => {
    const { code, res } = await Service.getUserToken();
    if (code === "error") {
      AppErrorActions.setFatalAppError(Error(res));
      return null;
    } else if (
      code === "NotAuthorizedException" ||
      code === "UserNotFoundException"
    ) {
      setIsCogniteAuthCheckDone(true);
      return null;
    }

    const tokenObj = res;
    if (tokenObj) {
      CognitoAuthActions.setToken({ tokenObj });
      const idToken = tokenObj.idToken.getJwtToken();

      /**
       * 구글로그인으로 회원가입을 막 마친 사용자는
       * 1) cognito 토큰은 이미 생성해서 저장중인 상태이지만,
       * 2) db에 사용자등록은 안 되어있는 상황이기에
       * GOOGLE_CALLBACK_PATH 에 해당하는 페이지로 이동후 db에 회원가입 등록 절차를 마쳐야 한다.
       * 그래서 이런 미등록 사용자의 경우 하단의 initMyProfile() 을 실행시 에러가 발생되기에
       * 여기서 사용자정보 저장 함수 실행전 이 함수가 종료되도록 구현된 상태.
       */
      if (window.location.pathname === GOOGLE_CALLBACK_PATH) {
        setIsCogniteAuthCheckDone(true);
        return null;
      }

      let userProfile;
      try {
        userProfile = await UserProfileActions.initMyProfile(idToken);
        const { success, location } = userProfile;
        if (!success) {
          await forceRemoveToken();
          window.location.replace(window.location.href);
          return null;
        }
        await UserDeviceActions.editViewerLocation(location);
      } catch (error) {
        AppErrorActions.setSafeAppError(error);
        setIsBackendServerBroken(true);
      }

      if (!userProfile) {
        const errObj = Error("null user profile");
        AppErrorActions.setSafeAppError(errObj);
        setIsBackendServerBroken(true);
      }

      MyAccountActions.setLogIn({
        email: userProfile.email,
        username: userProfile.username,
        idToken
      });
      CognitoAuthActions.setAuthData({
        isEmailAuthUser: userProfile.isEmailAuthUser,
        isGoogleAuthUser: userProfile.isGoogleAuthUser
      });
      GoogleAnalyticsActions.setUserInfoForGA({
        gaUserId: userProfile.gaUserId,
        location: userProfile.location,
        gender: userProfile.gender,
        birthday: userProfile.birthday
      });
    }

    GoogleAnalyticsActions.startAnalyzingNewUser();

    setIsCogniteAuthCheckDone(true);
  };

  return (
    <ErrorCatcher isBackendServerBroken={isBackendServerBroken}>
      <CookiesProvider>
        <DynamicHeader />
        <RootSnackbar />
        <BrowserRouter>
          <ScrollToTop>
            <LogOutContainer />
            <NoticeModalView />
            {isCogniteAuthCheckDone && isTokenValidationChekDone && (
              <MainRouter />
            )}
          </ScrollToTop>
        </BrowserRouter>
      </CookiesProvider>
    </ErrorCatcher>
  );
}

export default withStore({
  stateMap: {
    myAccount: ["username"],
    userDevice: ["viewerLocation"]
  },
  dispatchMap: [
    "myAccount",
    "userDevice",
    "userProfile",
    "cognitoAuth",
    "appUpdate",
    "googleAnalytics",
    "appError"
  ]
})(withToken(App));
